import {createAction, createAsyncThunk, createSlice, PayloadAction,} from "@reduxjs/toolkit";
import {ApplicationService} from "@/service/ApplicationService.ts";
import {ActionType} from "@/util/type/store.tsx";
import {Application, ReadApplicationResponse} from "@/model/response/application/ReadApplicationResponse.ts";
import {ReadApplicationByWorkSpaceIdRequest} from "@/model/request/application/ReadApplicationByWorkSpaceIdRequest.ts";
import {CreateApplication} from "@/model/request/application/createApplication.ts";
import {DeleteApplication} from "@/model/request/application/deleteApplication.ts";
import {BaseResponse} from "@/model/response/BaseResponse.ts";
import {UpdateApplication} from "@/model/request/application/updateApplication.ts";

export const selectApplication = createAction<Application>(
    "application/selectApplication"
);

export interface ApplicationState {
    loading: boolean;
    applications: Application[];
    selectedApplication: Application | null;
    applicationsByWorkSpaceId: Application []
}

const initialState: ApplicationState = {
    loading: false,
    applications: [] as Application[],
    selectedApplication: {} as Application,
    applicationsByWorkSpaceId: [] as Application[]
};

const action = {
    createApplication: createAsyncThunk(
        "application/create",
        async (payload: ActionType<CreateApplication>, thunkAPI) => {
            try {
                return (await ApplicationService.create(thunkAPI, payload.data)).data;
            } catch (error: any) {
                return thunkAPI.rejectWithValue(error.response.data)
            }

        }
    ),
    updateApplication: createAsyncThunk(
        "application/update",
        async (payload: ActionType<UpdateApplication>, thunkAPI) => {
            try {
                return (await ApplicationService.update(thunkAPI, payload.data)).data;
            } catch (error: any) {
                return thunkAPI.rejectWithValue(error.response.data)
            }

        }
    ),
    readApplication: createAsyncThunk(
        "application/read",
        async (_, thunkAPI) => {
            return (await ApplicationService.read(thunkAPI)).data;
        }
    ),
    deleteApplication: createAsyncThunk("application/delete", async (payload: ActionType<DeleteApplication>, thunkAPI) => {
        return (await ApplicationService.delete(thunkAPI, payload.data)).data
    }),
    readByWorkspaceId: createAsyncThunk(
        "application/readByWorkspaceId",
        async (payload: ReadApplicationByWorkSpaceIdRequest, thunkAPI) => {
            return (await ApplicationService.readByWorkspaceId(thunkAPI, payload)).data
        }
    ),
};


const slice = createSlice({
    name: "application",
    initialState,
    reducers: {
        setLoading(state, action) {
            state.loading = action.payload;
        },
        setApplications(state, action: PayloadAction<Application[]>) {
            state.applications = action.payload;
        },
        selectedApplication(state, action: PayloadAction<ApplicationState["selectedApplication"]>) {
            state.selectedApplication = action.payload;
        },
        setSelectedApplicationsByWorkSpaceId(state, action: PayloadAction<ApplicationState["applicationsByWorkSpaceId"]>) {
            state.applicationsByWorkSpaceId = action.payload;
        },
        reset: () => ({...initialState})

    },
    extraReducers: (builder) => {
        builder
            .addCase(action.createApplication.pending, (state) => {
                state.loading = true;
            })
            .addCase(action.createApplication.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload.responseCode === "00")
                    action.meta.arg.onSuccess?.(action.payload);
                else action.meta.arg.onError?.(action.payload);
            }).addCase(action.updateApplication.pending, (state) => {
            state.loading = true
        }).addCase(action.updateApplication.fulfilled, (state, action) => {
            state.loading = false
            if (action.payload.responseCode === "00")
                action.meta.arg.onSuccess?.(action.payload);
            else action.meta.arg.onError?.(action.payload);
        }).addCase(action.deleteApplication.pending, (state) => {
            state.loading = true
        }).addCase(action.deleteApplication.fulfilled, (state, action) => {
            const response = action.payload as BaseResponse;
            state.loading = false
            if (response.responseCode === "00") {
                action.meta.arg.onSuccess?.(action.payload);
            } else {
                action.meta.arg.onError?.(action.payload);
            }
        })
            .addCase(action.readApplication.pending, (state) => {
                state.loading = true;
            })
            .addCase(action.readApplication.fulfilled, (state, action: PayloadAction<ReadApplicationResponse>) => {
                state.loading = false;
                if (action.payload.responseCode === "00") {
                    state.applications = action.payload.data;
                }
            })
            .addCase(action.readByWorkspaceId.pending, (state) => {
                state.loading = true;
            })
            .addCase(action.readByWorkspaceId.fulfilled, (state, action) => {
                state.loading = false;
                if (action.payload.responseCode === "00") {
                    state.applicationsByWorkSpaceId = action.payload.data
                }
            })
            .addCase(
                selectApplication,
                (state, action: PayloadAction<Application>) => {
                    state.selectedApplication = action.payload;
                }
            );
    },
});

export const application = {
    reducer: slice.reducer,
    action,
    mutation: slice.actions,
};
